import { useCallback, useMemo } from 'react';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useToast } from '@givebutter/primitives/toast';
import { maskDecimal, maskWholeNumber, unmaskNumber } from '@givebutter/util';
import { faEllipsisV } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createItemBid, fetchAuctionBidders, fetchCampaignItemBids } from 'api/campaigns';
import { useGivebutterForm } from 'components/form/useGivebutterForm';
import { BidItemStatusPill } from 'components/pages/campaign-settings/campaign-auction/BidStatus';
import * as BidsOptions from 'components/pages/campaign-settings/campaign-auction/item-details/bids/BidsOptions';
import { TableConstants, useReactTableState } from 'components/table';
import { EmptyData } from 'components/util';
import { useCurrencySymbol } from 'customHooks/useCurrencySymbol';
import { useCurrentAuction } from 'customHooks/useCurrentAuction';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { useCurrentCampaignItem } from 'customHooks/useCurrentCampaignItem';
import { isoDateFromServer } from 'helpers/dates';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { DateTime } from 'luxon';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { AuctionItem, Bid } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface ManualBidForm {
    amount: string;
    bidder: { id: string; label: string; value: string }[];
}

const columnHelper = createColumnHelper<Bid>();
const allBidsParams = { page: 1, limit: 9999 };
const defaultFormValues: ManualBidForm = {
    amount: '',
    bidder: [],
};

const useBids = () => {
    const toast = useToast();
    const queryClient = useQueryClient();
    const { pagination, setPagination } = useReactTableState();
    const { data: campaign } = useCurrentCampaign();
    const { data: item } = useCurrentCampaignItem<AuctionItem>();
    const { cancelBid = false } = useFlags();
    const currencySymbol = useCurrencySymbol();
    const { data: auction } = useCurrentAuction();

    const campaignId = Number(campaign.id);
    const auctionId = auction.id;
    const itemId = Number(item.id);

    const formMethods = useGivebutterForm<ManualBidForm>({
        defaultValues: defaultFormValues,
    });

    const invalidateQueries = useCallback(
        () =>
            queryClient.invalidateQueries({
                queryKey: campaignQueryKeys.item(campaignId, itemId),
                type: 'all',
            }),
        [campaignId, itemId, queryClient],
    );

    const { data: bids } = useSuspenseQuery({
        queryKey: campaignQueryKeys.itemBids(campaignId, itemId, {
            page: pagination.pageIndex + 1,
        }),
        queryFn: () =>
            fetchCampaignItemBids({ campaignId, itemId, page: pagination.pageIndex + 1 }),
    });

    const { data: bidders = [] } = useSuspenseQuery({
        queryKey: campaignQueryKeys.itemBids(campaignId, itemId, allBidsParams),
        queryFn: () => fetchAuctionBidders(campaignId, auctionId, allBidsParams),
        select: data =>
            data.data.map(bidder => ({
                id: bidder.id,
                name: `${bidder.first_name} ${bidder.last_name}`,
                number: bidder.number,
                payment_methods: bidder.payment_methods,
            })),
    });

    const { isPending: isCreatingBid, mutate: createBid } = useMutation({
        mutationFn: ({ amount, bidderNumber }: { amount: number; bidderNumber: number }) =>
            createItemBid(campaignId, itemId, {
                initial_amount: amount,
                bidder_number: bidderNumber,
            }),
        onSuccess: () => {
            formMethods.reset(defaultFormValues);
            invalidateQueries();
            toast.success(`Bid created successfully`);
        },

        onError: (e: GivebutterAxiosError) => {
            toast.error(e.response?.data?.message ?? `Unable to add bid`);
        },
    });

    const onSubmitManualBid = formMethods.handleSubmit((data: ManualBidForm) => {
        createBid({
            amount: unmaskNumber(data.amount),
            bidderNumber: Number(data.bidder[0].value),
        });
    });

    const columns = useMemo(
        () => [
            columnHelper.accessor('amount', {
                id: 'amount',
                header: 'Amount',
                cell: info => {
                    return (
                        <p className={'whitespace-nowrap text-sm font-medium'}>
                            {currencySymbol}
                            {maskDecimal(info.getValue())}
                        </p>
                    );
                },
            }),
            columnHelper.accessor('bidder', {
                id: 'bidder',
                header: 'Bidder Name',
                cell: info => {
                    const { first_name: firstName, last_name: lastName } = info.getValue() || {};
                    return <p className={'text-sm font-medium'}>{`${firstName} ${lastName}`}</p>;
                },
            }),
            columnHelper.accessor('bidder.number', {
                id: 'number',
                header: 'Bidder ID #',
                cell: info => {
                    const bidderId = info.getValue();

                    return (
                        <p className={'text-sm font-medium'}>
                            {bidderId ? maskWholeNumber(bidderId) : <EmptyData />}
                        </p>
                    );
                },
            }),
            columnHelper.accessor('confirmed_at', {
                id: 'confirmed-date',
                header: 'Date & Time',
                cell: info => {
                    const bid = info.row.original;
                    const bidDate = bid.confirmed ? bid.confirmed_at : bid.bid_at;
                    const date = isoDateFromServer(bidDate).toLocaleString(DateTime.DATETIME_MED);

                    return <p className={'text-xs'}>{date}</p>;
                },
            }),
            columnHelper.accessor('status', {
                id: 'status',
                header: 'Status',
                cell: info => {
                    return (
                        <BidItemStatusPill
                            className={
                                'w-fit whitespace-nowrap rounded-full px-2 py-1 text-center text-sm'
                            }
                            status={info.getValue()}
                        />
                    );
                },
                meta: {
                    className: 'p-0',
                },
            }),
            columnHelper.display({
                id: TableConstants.Actions,
                cell: info => (
                    <BidsOptions.Root
                        auctionItem={item}
                        campaign={campaign}
                        bid={info.row.original}
                    >
                        <BidsOptions.Trigger
                            className={'float-right rounded px-2 py-1 hover:bg-gray-300'}
                        >
                            <FontAwesomeIcon icon={faEllipsisV} />
                        </BidsOptions.Trigger>
                        <BidsOptions.Content align={'end'}>
                            <BidsOptions.Cancel onCancelBid={invalidateQueries} />
                        </BidsOptions.Content>
                    </BidsOptions.Root>
                ),
            }),
        ],
        [currencySymbol, item, campaign, invalidateQueries],
    );

    const table = useReactTable({
        data: bids?.data ?? [],
        columns,
        pageCount: bids?.meta.last_page ?? -1,
        getCoreRowModel: getCoreRowModel(),
        state: {
            pagination,
            columnVisibility: {
                action: cancelBid,
            },
        },
        onPaginationChange: setPagination,
        manualPagination: true,
        enableSorting: false,
        enableHiding: false,
        enableColumnPinning: false,
    });

    return {
        table,
        bidders,
        item,
        createBid,
        formMethods,
        onSubmitManualBid,
        isCreatingBid,
    };
};

export { useBids };
